import { useCallback, useEffect, useRef, useState } from 'react'
import { getProjectAutofillDraft } from '../../../../../../../services/projectAutofillDraftService.ts'
import {
  projectAutofillDraftApiToCardPayload,
  type ProjectAutofillPayload
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  buildDocumentationReviewContext,
  type DocumentationReviewContext
} from '../../../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'

export type UseProjectAutofillWorkflowArgs = {
  draftId: string | undefined
  initialPayload: ProjectAutofillPayload
  onDraftHydrated: (payload: ProjectAutofillPayload) => void
}

export function useProjectAutofillWorkflow(args: UseProjectAutofillWorkflowArgs) {
  const { draftId, initialPayload, onDraftHydrated } = args

  const [documentationReview, setDocumentationReview] = useState<DocumentationReviewContext>(() =>
    buildDocumentationReviewContext(initialPayload.draft?.metadata)
  )
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const applyDraftResponse = useCallback(
    (raw: unknown, id: string) => {
      const payload = projectAutofillDraftApiToCardPayload(raw, id)
      const review = buildDocumentationReviewContext(payload.draft?.metadata)
      if (mountedRef.current) {
        setDocumentationReview(review)
      }
      onDraftHydrated(payload)
      return payload
    },
    [onDraftHydrated]
  )

  const refreshDraft = useCallback(async () => {
    if (!draftId) return null
    try {
      const raw = await getProjectAutofillDraft(draftId)
      return applyDraftResponse(raw, draftId)
    } catch {
      return null
    }
  }, [draftId, applyDraftResponse])

  useEffect(() => {
    if (!draftId) return
    void refreshDraft()
  }, [draftId, refreshDraft])

  return {
    documentationReview,
    canPublishReview: documentationReview.canPublishReview,
    refreshDraft
  }
}
